import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ResponseButtonConfigEditor } from '../components/ResponseButtonConfigEditor';
import { trpc } from '../lib/trpc';
import { useProject } from '../context';
import {
  createEmptyResponseButtonConfig,
  prepareResponseButtonConfigForSave,
  RESPONSE_BUTTON_ROLLOUT_GUIDANCE,
  tryHydrateResponseButtonConfig,
  type ResponseButtonConfig,
} from '../lib/responseButtons';

/**
 * Type for media configuration in onboarding steps.
 */
interface OnboardingMedia {
  type: 'image' | 'video' | 'audio' | 'document';
  source: string;
  caption?: string;
  filename?: string;
  mimeType?: string;
}

/**
 * Type for onboarding step configuration.
 * Matches backend onboardingStepSchema.
 */
interface OnboardingStep {
  id: string;
  type: 'text' | 'buttons' | 'list' | 'media' | 'time_picker' | 'payment' | 'informational';
  message: string;
  field?: string;
  validation?: {
    type: string;
    pattern?: string;
    min?: number;
    max?: number;
  };
  // Media step configuration
  media?: OnboardingMedia;
  // Button text for informational steps
  continueButtonText?: string;
}

/**
 * Type for onboarding flow configuration.
 */
interface OnboardingFlow {
  steps: OnboardingStep[];
  completionMessage: string;
}

/**
 * Generated bot configuration from AI.
 */
interface GeneratedBotConfig {
  name: string;
  slug: string;
  description: string;
  icon: string;
  color: string;
  systemPrompt: string;
  voiceStyle: string;
  dailyMessageTemplate: string;
  onboardingFlow: OnboardingFlow;
  enabledTools: string[];
  toolConfigs: Record<string, unknown>;
  overrideAiModel: string;
  overrideAiMaxTokens: number | null;
  overrideTrialDays: number | null;
  responseButtonConfig: ResponseButtonConfig | null;
}

/**
 * Bot create page component with AI-powered wizard.
 *
 * Two-step wizard:
 * 1. Enter a natural language prompt to describe the bot
 * 2. Review and edit the AI-generated configuration
 */
export function BotCreatePage() {
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const { selectBot } = useProject();

  // Wizard state
  const [currentStep, setCurrentStep] = useState<1 | 2>(1);

  // Step 1: Prompt input state
  const [prompt, setPrompt] = useState('');

  // Step 2: Generated config state
  const [generatedConfig, setGeneratedConfig] = useState<GeneratedBotConfig | null>(null);
  const [editedConfig, setEditedConfig] = useState<GeneratedBotConfig | null>(null);
  const [responseButtonConfigNotice, setResponseButtonConfigNotice] = useState<string | null>(null);
  const [responseButtonConfigError, setResponseButtonConfigError] = useState<string | null>(null);

  // AI generation mutation
  const generateMutation = trpc.bots.generateFromPrompt.useMutation({
    onSuccess: (data) => {
      // Add default values for override fields not present in the AI-generated config
      // Cast through unknown because the server may return various step type values
      const baseConfig = data.config as unknown as Omit<GeneratedBotConfig, 'overrideAiModel' | 'overrideAiMaxTokens' | 'overrideTrialDays'>;
      const hydratedResponseButtonConfig = tryHydrateResponseButtonConfig(baseConfig.responseButtonConfig);
      const config: GeneratedBotConfig = {
        ...baseConfig,
        overrideAiModel: '',
        overrideAiMaxTokens: null,
        overrideTrialDays: null,
        responseButtonConfig: hydratedResponseButtonConfig.success
          ? hydratedResponseButtonConfig.config
          : createEmptyResponseButtonConfig(),
      };
      setGeneratedConfig(config);
      setEditedConfig(config);
      setResponseButtonConfigError(null);
      setResponseButtonConfigNotice(
        hydratedResponseButtonConfig.success
          ? null
          : `AI-generated response button config was invalid and has been reset to an empty editable config.\n${hydratedResponseButtonConfig.error}`,
      );
      setCurrentStep(2);
    },
  });

  // Create bot mutation
  const createBotMutation = trpc.bots.create.useMutation({
    onSuccess: (data) => {
      utils.bots.list.invalidate();
      // Select the newly created bot and navigate to the test page
      selectBot(data.id);
      navigate('/bot/test');
    },
  });

  /**
   * Handle Step 1: Generate bot config from prompt
   */
  const handleGenerate = (e: React.FormEvent) => {
    e.preventDefault();
    generateMutation.mutate({ prompt });
  };

  /**
   * Handle Step 2: Create bot from edited config
   */
  const handleCreateBot = () => {
    if (!editedConfig) return;

    let responseButtonConfig: ResponseButtonConfig | undefined;

    if (editedConfig.responseButtonConfig) {
      const responseButtonConfigResult = prepareResponseButtonConfigForSave(editedConfig.responseButtonConfig);
      if (!responseButtonConfigResult.success) {
        setResponseButtonConfigError(responseButtonConfigResult.error);
        return;
      }

      responseButtonConfig = responseButtonConfigResult.config;
    }

    setResponseButtonConfigError(null);

    createBotMutation.mutate({
      name: editedConfig.name,
      slug: editedConfig.slug,
      description: editedConfig.description || undefined,
      icon: editedConfig.icon || undefined,
      color: editedConfig.color || undefined,
      systemPrompt: editedConfig.systemPrompt,
      voiceStyle: editedConfig.voiceStyle || undefined,
      dailyMessageTemplate: editedConfig.dailyMessageTemplate || undefined,
      onboardingFlow: editedConfig.onboardingFlow,
      enabledTools: editedConfig.enabledTools.length > 0 ? editedConfig.enabledTools : undefined,
      toolConfigs: Object.keys(editedConfig.toolConfigs).length > 0 ? editedConfig.toolConfigs : undefined,
      overrideAiModel: editedConfig.overrideAiModel || undefined,
      overrideAiMaxTokens: editedConfig.overrideAiMaxTokens ?? undefined,
      overrideTrialDays: editedConfig.overrideTrialDays ?? undefined,
      responseButtonConfig,
    });
  };

  /**
   * Go back to Step 1 to regenerate
   */
  const handleBackToPrompt = () => {
    setCurrentStep(1);
  };

  /**
   * Update a field in the edited config
   */
  const updateConfig = <K extends keyof GeneratedBotConfig>(
    field: K,
    value: GeneratedBotConfig[K]
  ) => {
    if (!editedConfig) return;
    setEditedConfig({ ...editedConfig, [field]: value });
  };

  /**
   * Generate slug from name
   */
  const generateSlug = (value: string) => {
    return value
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  };

  /**
   * Update an onboarding step
   */
  const updateOnboardingStep = (index: number, field: keyof OnboardingStep, value: string) => {
    if (!editedConfig) return;
    const newSteps = [...editedConfig.onboardingFlow.steps];
    newSteps[index] = { ...newSteps[index], [field]: value };
    updateConfig('onboardingFlow', {
      ...editedConfig.onboardingFlow,
      steps: newSteps,
    });
  };

  /**
   * Update the media source URL for a media step.
   */
  const updateOnboardingStepMediaUrl = (index: number, url: string) => {
    if (!editedConfig) return;
    const newSteps = [...editedConfig.onboardingFlow.steps];
    const step = newSteps[index];
    newSteps[index] = {
      ...step,
      media: {
        type: step.media?.type || 'image',
        source: url,
        caption: step.media?.caption,
        filename: step.media?.filename,
        mimeType: step.media?.mimeType,
      },
    };
    updateConfig('onboardingFlow', {
      ...editedConfig.onboardingFlow,
      steps: newSteps,
    });
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <button onClick={() => navigate('/bots')} style={styles.backButton}>
          &larr; Back to Bots
        </button>
        <h1 style={styles.title}>Create Bot with AI</h1>
      </div>

      {/* Step Indicator */}
      <div style={styles.stepIndicator}>
        <div style={styles.stepItem}>
          <div
            style={{
              ...styles.stepCircle,
              ...(currentStep >= 1 ? styles.stepCircleActive : {}),
            }}
          >
            1
          </div>
          <span style={styles.stepLabel}>Describe Your Bot</span>
        </div>
        <div style={styles.stepConnector} />
        <div style={styles.stepItem}>
          <div
            style={{
              ...styles.stepCircle,
              ...(currentStep >= 2 ? styles.stepCircleActive : {}),
            }}
          >
            2
          </div>
          <span style={styles.stepLabel}>Review & Create</span>
        </div>
      </div>

      {/* Step 1: Prompt Input */}
      {currentStep === 1 && (
        <form onSubmit={handleGenerate} style={styles.form}>
          <div style={styles.card}>
            <h2 style={styles.cardTitle}>Describe Your Bot</h2>
            <p style={styles.cardDescription}>
              Tell us about the bot you want to create. Be specific about its purpose,
              personality, and how it should interact with users.
            </p>

            {generateMutation.error && (
              <div style={styles.error}>
                Error generating bot: {generateMutation.error.message}
              </div>
            )}

            <div style={styles.field}>
              <label style={styles.label}>Bot Description *</label>
              <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="Example: Create a fitness coaching bot that helps users stay motivated with their workout routines. It should be encouraging and supportive, ask about their fitness goals, and send daily motivational messages."
                style={styles.promptTextarea}
                rows={6}
                required
                minLength={10}
              />
              <span style={styles.hint}>
                Minimum 10 characters. Be detailed about the bot's purpose, tone, and target audience.
              </span>
            </div>

            <div style={styles.examplesSection}>
              <h4 style={styles.examplesTitle}>Example Prompts</h4>
              <div style={styles.examplesGrid}>
                <button
                  type="button"
                  style={styles.exampleButton}
                  onClick={() =>
                    setPrompt(
                      'Create a meditation and mindfulness coach that helps users develop a daily meditation practice. It should be calm and peaceful, guide users through breathing exercises, and track their meditation streaks.'
                    )
                  }
                >
                  <span style={styles.exampleIcon}>🧘</span>
                  <span>Meditation Coach</span>
                </button>
                <button
                  type="button"
                  style={styles.exampleButton}
                  onClick={() =>
                    setPrompt(
                      'Create a language learning buddy that helps users practice Spanish conversation. It should be patient and encouraging, correct mistakes gently, and adapt to the user\'s proficiency level.'
                    )
                  }
                >
                  <span style={styles.exampleIcon}>🗣️</span>
                  <span>Language Buddy</span>
                </button>
                <button
                  type="button"
                  style={styles.exampleButton}
                  onClick={() =>
                    setPrompt(
                      'Create a productivity coach that helps users manage their tasks and time. It should be organized and motivating, help set priorities, and send reminders for important deadlines.'
                    )
                  }
                >
                  <span style={styles.exampleIcon}>📋</span>
                  <span>Productivity Coach</span>
                </button>
                <button
                  type="button"
                  style={styles.exampleButton}
                  onClick={() =>
                    setPrompt(
                      'Create a nutrition advisor that helps users track their eating habits and make healthier choices. It should be supportive without being judgmental, provide meal suggestions, and celebrate healthy milestones.'
                    )
                  }
                >
                  <span style={styles.exampleIcon}>🥗</span>
                  <span>Nutrition Advisor</span>
                </button>
              </div>
            </div>

            <div style={styles.actions}>
              <button
                type="button"
                onClick={() => navigate('/bots')}
                style={styles.cancelButton}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={generateMutation.isPending || prompt.length < 10}
                style={{
                  ...styles.generateButton,
                  ...(generateMutation.isPending || prompt.length < 10
                    ? styles.buttonDisabled
                    : {}),
                }}
              >
                {generateMutation.isPending ? (
                  <>
                    <span style={styles.spinner} />
                    Generating...
                  </>
                ) : (
                  <>
                    <span>✨</span>
                    Generate Bot Config
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      )}

      {/* Step 2: Review & Create */}
      {currentStep === 2 && editedConfig && (
        <div style={styles.form}>
          <div style={styles.card}>
            <h2 style={styles.cardTitle}>Review Generated Configuration</h2>
            <p style={styles.cardDescription}>
              Review and edit the AI-generated configuration before creating your bot.
            </p>

            {createBotMutation.error && (
              <div style={styles.error}>
                Error creating bot: {createBotMutation.error.message}
              </div>
            )}

            {/* Basic Info Section */}
            <div style={styles.configSection}>
              <h3 style={styles.configSectionTitle}>Basic Information</h3>

              <div style={styles.fieldsGrid}>
                <div style={styles.field}>
                  <label style={styles.label}>Name *</label>
                  <input
                    type="text"
                    value={editedConfig.name}
                    onChange={(e) => {
                      updateConfig('name', e.target.value);
                      if (editedConfig.slug === generateSlug(generatedConfig?.name || '')) {
                        updateConfig('slug', generateSlug(e.target.value));
                      }
                    }}
                    style={styles.input}
                    required
                  />
                </div>

                <div style={styles.field}>
                  <label style={styles.label}>Slug *</label>
                  <input
                    type="text"
                    value={editedConfig.slug}
                    onChange={(e) => updateConfig('slug', generateSlug(e.target.value))}
                    style={styles.input}
                    required
                  />
                  <span style={styles.hint}>URL-friendly identifier</span>
                </div>

                <div style={styles.field}>
                  <label style={styles.label}>Icon</label>
                  <input
                    type="text"
                    value={editedConfig.icon}
                    onChange={(e) => updateConfig('icon', e.target.value)}
                    style={styles.input}
                    maxLength={4}
                  />
                </div>

                <div style={styles.field}>
                  <label style={styles.label}>Color</label>
                  <div style={styles.colorInput}>
                    <input
                      type="color"
                      value={editedConfig.color}
                      onChange={(e) => updateConfig('color', e.target.value)}
                      style={styles.colorPicker}
                    />
                    <input
                      type="text"
                      value={editedConfig.color}
                      onChange={(e) => updateConfig('color', e.target.value)}
                      style={{ ...styles.input, flex: 1 }}
                      placeholder="#000000"
                    />
                  </div>
                </div>
              </div>

              <div style={styles.field}>
                <label style={styles.label}>Description</label>
                <textarea
                  value={editedConfig.description}
                  onChange={(e) => updateConfig('description', e.target.value)}
                  style={styles.textarea}
                  rows={2}
                />
              </div>
            </div>

            {/* Personality Section */}
            <div style={styles.configSection}>
              <h3 style={styles.configSectionTitle}>Personality & Behavior</h3>

              <div style={styles.field}>
                <label style={styles.label}>System Prompt *</label>
                <textarea
                  value={editedConfig.systemPrompt}
                  onChange={(e) => updateConfig('systemPrompt', e.target.value)}
                  style={styles.textarea}
                  rows={6}
                  required
                />
                <span style={styles.hint}>
                  Defines the bot's personality, role, and behavior guidelines
                </span>
              </div>

              <div style={styles.field}>
                <label style={styles.label}>Voice Style</label>
                <input
                  type="text"
                  value={editedConfig.voiceStyle}
                  onChange={(e) => updateConfig('voiceStyle', e.target.value)}
                  style={styles.input}
                  placeholder="e.g., warm, professional, casual"
                />
              </div>

              <div style={styles.field}>
                <label style={styles.label}>Daily Message Template</label>
                <textarea
                  value={editedConfig.dailyMessageTemplate}
                  onChange={(e) => updateConfig('dailyMessageTemplate', e.target.value)}
                  style={styles.textarea}
                  rows={3}
                  placeholder="Use {{name}} for user's name"
                />
              </div>
            </div>

            {/* Onboarding Flow Section */}
            <div style={styles.configSection}>
              <h3 style={styles.configSectionTitle}>Onboarding Flow</h3>

              <div style={styles.stepsContainer}>
                {editedConfig.onboardingFlow.steps.map((step, index) => (
                  <div key={step.id} style={styles.stepCard}>
                    <div style={styles.stepCardHeader}>
                      <span style={styles.stepNumber}>{index + 1}</span>
                      <select
                        value={step.type}
                        onChange={(e) =>
                          updateOnboardingStep(
                            index,
                            'type',
                            e.target.value as OnboardingStep['type']
                          )
                        }
                        style={styles.stepTypeSelect}
                      >
                        <option value="text">Text</option>
                        <option value="buttons">Buttons</option>
                        <option value="list">List</option>
                        <option value="media">Media</option>
                        <option value="time_picker">Time Picker</option>
                        <option value="payment">Payment</option>
                        <option value="informational">Informational</option>
                      </select>
                      {step.field && (
                        <span style={styles.stepField}>Field: {step.field}</span>
                      )}
                    </div>
                    <textarea
                      value={step.message}
                      onChange={(e) => updateOnboardingStep(index, 'message', e.target.value)}
                      style={styles.stepMessageInput}
                      rows={2}
                    />
                    {step.type === 'media' && (
                      <div style={styles.mediaUrlField}>
                        <label style={styles.mediaUrlLabel}>Media URL</label>
                        <input
                          type="text"
                          value={step.media?.source || ''}
                          onChange={(e) => updateOnboardingStepMediaUrl(index, e.target.value)}
                          style={styles.mediaUrlInput}
                          placeholder="Enter the media URL (image, video, audio, or document)..."
                        />
                        <span style={styles.mediaUrlHint}>
                          URL of the media file to display in this step
                        </span>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <div style={styles.field}>
                <label style={styles.label}>Completion Message</label>
                <textarea
                  value={editedConfig.onboardingFlow.completionMessage}
                  onChange={(e) =>
                    updateConfig('onboardingFlow', {
                      ...editedConfig.onboardingFlow,
                      completionMessage: e.target.value,
                    })
                  }
                  style={styles.textarea}
                  rows={2}
                />
              </div>
            </div>

            {/* Advanced Settings Section */}
            <div style={styles.configSection}>
              <h3 style={styles.configSectionTitle}>Advanced Settings</h3>
              <p style={styles.sectionDescription}>
                Optional overrides for AI model, token limits, and trial period.
              </p>

              <div style={styles.fieldsGrid}>
                <div style={styles.field}>
                  <label style={styles.label}>AI Model Override</label>
                  <input
                    type="text"
                    value={editedConfig.overrideAiModel}
                    onChange={(e) => updateConfig('overrideAiModel', e.target.value)}
                    style={styles.input}
                    placeholder="e.g., google/gemini-3-flash-preview"
                  />
                  <span style={styles.hint}>
                    OpenRouter agent ID format (e.g., google/gemini-3-flash-preview). Leave empty to use system default.
                  </span>
                </div>

                <div style={styles.field}>
                  <label style={styles.label}>Max Tokens Override</label>
                  <input
                    type="number"
                    value={editedConfig.overrideAiMaxTokens ?? ''}
                    onChange={(e) =>
                      updateConfig(
                        'overrideAiMaxTokens',
                        e.target.value ? parseInt(e.target.value, 10) : null
                      )
                    }
                    style={styles.input}
                    placeholder="e.g., 4096"
                    min={1}
                  />
                  <span style={styles.hint}>
                    Maximum tokens for AI responses
                  </span>
                </div>

                <div style={styles.field}>
                  <label style={styles.label}>Trial Days Override</label>
                  <input
                    type="number"
                    value={editedConfig.overrideTrialDays ?? ''}
                    onChange={(e) =>
                      updateConfig(
                        'overrideTrialDays',
                        e.target.value ? parseInt(e.target.value, 10) : null
                      )
                    }
                    style={styles.input}
                    placeholder="e.g., 14"
                    min={1}
                  />
                  <span style={styles.hint}>
                    Custom trial period for this bot
                  </span>
                </div>
              </div>
            </div>

            <div style={styles.configSection}>
              <h3 style={styles.configSectionTitle}>Response Buttons</h3>
              <p style={styles.sectionDescription}>
                Configure WhatsApp reply buttons for daily, weekly, and chat surfaces without editing raw JSON. {RESPONSE_BUTTON_ROLLOUT_GUIDANCE}
              </p>
              <ResponseButtonConfigEditor
                value={editedConfig.responseButtonConfig ?? createEmptyResponseButtonConfig()}
                onChange={(value) => {
                  setResponseButtonConfigError(null);
                  updateConfig('responseButtonConfig', value);
                }}
                error={[responseButtonConfigNotice, responseButtonConfigError].filter(Boolean).join('\n\n') || null}
              />
            </div>

            {/* Actions */}
            <div style={styles.actions}>
              <button
                type="button"
                onClick={handleBackToPrompt}
                style={styles.secondaryButton}
              >
                &larr; Back to Prompt
              </button>
              <div style={styles.actionsSpacer} />
              <button
                type="button"
                onClick={() => navigate('/bots')}
                style={styles.cancelButton}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCreateBot}
                disabled={
                  createBotMutation.isPending ||
                  !editedConfig.name ||
                  !editedConfig.slug ||
                  !editedConfig.systemPrompt
                }
                style={{
                  ...styles.submitButton,
                  ...(createBotMutation.isPending ||
                  !editedConfig.name ||
                  !editedConfig.slug ||
                  !editedConfig.systemPrompt
                    ? styles.buttonDisabled
                    : {}),
                }}
              >
                {createBotMutation.isPending ? 'Creating...' : 'Create Bot'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    maxWidth: '800px',
  },
  header: {
    marginBottom: '24px',
  },
  backButton: {
    padding: '8px 16px',
    backgroundColor: 'transparent',
    border: 'none',
    color: '#3b82f6',
    fontSize: '14px',
    cursor: 'pointer',
    marginBottom: '12px',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
  },
  title: {
    fontSize: '28px',
    fontWeight: '600',
    color: '#1e293b',
  },

  // Step Indicator
  stepIndicator: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '32px',
    padding: '0 40px',
  },
  stepItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
  },
  stepCircle: {
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    backgroundColor: '#e2e8f0',
    color: '#64748b',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '14px',
    fontWeight: '600',
    transition: 'all 0.2s ease',
  },
  stepCircleActive: {
    backgroundColor: '#3b82f6',
    color: '#ffffff',
  },
  stepLabel: {
    fontSize: '13px',
    color: '#64748b',
    fontWeight: '500',
  },
  stepConnector: {
    flex: 1,
    height: '2px',
    backgroundColor: '#e2e8f0',
    margin: '0 16px',
    marginBottom: '24px',
    maxWidth: '120px',
  },

  // Form
  form: {
    width: '100%',
  },
  card: {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    padding: '24px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  },
  cardTitle: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#1e293b',
    marginBottom: '8px',
  },
  cardDescription: {
    fontSize: '14px',
    color: '#64748b',
    marginBottom: '24px',
    lineHeight: '1.5',
  },
  error: {
    padding: '12px 16px',
    backgroundColor: '#fef2f2',
    color: '#dc2626',
    borderRadius: '8px',
    marginBottom: '20px',
    fontSize: '14px',
  },
  field: {
    marginBottom: '20px',
  },
  fieldsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '16px',
    marginBottom: '16px',
  },
  label: {
    display: 'block',
    fontSize: '14px',
    fontWeight: '500',
    color: '#334155',
    marginBottom: '6px',
  },
  input: {
    width: '100%',
    padding: '10px 14px',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    fontSize: '14px',
    outline: 'none',
    boxSizing: 'border-box',
  },
  textarea: {
    width: '100%',
    padding: '10px 14px',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    fontSize: '14px',
    outline: 'none',
    resize: 'vertical',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
    lineHeight: '1.5',
  },
  promptTextarea: {
    width: '100%',
    padding: '14px 16px',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    fontSize: '15px',
    outline: 'none',
    resize: 'vertical',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
    lineHeight: '1.6',
  },
  hint: {
    display: 'block',
    fontSize: '12px',
    color: '#94a3b8',
    marginTop: '4px',
  },
  colorInput: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  colorPicker: {
    width: '40px',
    height: '40px',
    padding: '0',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    cursor: 'pointer',
  },

  // Examples
  examplesSection: {
    marginTop: '24px',
    paddingTop: '24px',
    borderTop: '1px solid #f1f5f9',
  },
  examplesTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#64748b',
    marginBottom: '12px',
  },
  examplesGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '12px',
  },
  exampleButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '12px 16px',
    backgroundColor: '#f8fafc',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    fontSize: '14px',
    color: '#334155',
    cursor: 'pointer',
    textAlign: 'left',
    transition: 'all 0.15s ease',
  },
  exampleIcon: {
    fontSize: '20px',
  },

  // Actions
  actions: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
    borderTop: '1px solid #f1f5f9',
    paddingTop: '20px',
    marginTop: '24px',
    alignItems: 'center',
  },
  actionsSpacer: {
    flex: 1,
  },
  cancelButton: {
    padding: '10px 20px',
    backgroundColor: '#f8fafc',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    fontSize: '14px',
    color: '#475569',
    cursor: 'pointer',
  },
  secondaryButton: {
    padding: '10px 20px',
    backgroundColor: 'transparent',
    border: 'none',
    fontSize: '14px',
    color: '#3b82f6',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  generateButton: {
    padding: '10px 24px',
    backgroundColor: '#8b5cf6',
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  submitButton: {
    padding: '10px 20px',
    backgroundColor: '#3b82f6',
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  buttonDisabled: {
    backgroundColor: '#94a3b8',
    cursor: 'not-allowed',
  },
  spinner: {
    display: 'inline-block',
    width: '14px',
    height: '14px',
    border: '2px solid #ffffff',
    borderTopColor: 'transparent',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },

  // Config Sections
  configSection: {
    marginBottom: '32px',
    paddingBottom: '24px',
    borderBottom: '1px solid #f1f5f9',
  },
  configSectionTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#1e293b',
    marginBottom: '8px',
  },
  sectionDescription: {
    fontSize: '14px',
    color: '#64748b',
    marginBottom: '16px',
    lineHeight: '1.5',
  },

  // Onboarding Steps
  stepsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    marginBottom: '20px',
  },
  stepCard: {
    backgroundColor: '#f8fafc',
    borderRadius: '8px',
    padding: '16px',
    borderLeft: '4px solid #3b82f6',
  },
  stepCardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '12px',
  },
  stepNumber: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    backgroundColor: '#3b82f6',
    color: '#ffffff',
    fontSize: '12px',
    fontWeight: '600',
  },
  stepTypeSelect: {
    padding: '6px 12px',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    fontSize: '13px',
    backgroundColor: '#ffffff',
    cursor: 'pointer',
  },
  stepField: {
    fontSize: '12px',
    color: '#64748b',
    fontFamily: 'monospace',
    backgroundColor: '#e2e8f0',
    padding: '4px 8px',
    borderRadius: '4px',
  },
  stepMessageInput: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    fontSize: '14px',
    outline: 'none',
    resize: 'vertical',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
    backgroundColor: '#ffffff',
  },
  // Media URL field styles for media step type
  mediaUrlField: {
    marginTop: '12px',
    paddingTop: '12px',
    borderTop: '1px dashed #e2e8f0',
  },
  mediaUrlLabel: {
    display: 'block',
    fontSize: '13px',
    fontWeight: '500',
    color: '#475569',
    marginBottom: '6px',
  },
  mediaUrlInput: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    fontSize: '14px',
    outline: 'none',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
    backgroundColor: '#ffffff',
  },
  mediaUrlHint: {
    display: 'block',
    fontSize: '12px',
    color: '#94a3b8',
    marginTop: '4px',
  },
};

export default BotCreatePage;
